import { HttpInterceptorFn } from '@angular/common/http';

/**
 * Fixes the pervasive "date shifts back 5 hours on save" bug.
 *
 * mat-datepicker hands components a local `Date` (e.g. 2026-06-08 00:00 PKT). When Angular's
 * HttpClient JSON-serializes the body, `Date.toJSON()` calls `toISOString()` which converts to
 * UTC — so 2026-06-08 00:00 becomes "2026-06-07T19:00:00Z" and the backend stores the wrong day.
 *
 * This interceptor walks every outgoing JSON body and replaces each `Date` with a string built
 * from its LOCAL components and NO timezone suffix (e.g. "2026-06-08T00:00:00"). ASP.NET then
 * parses it as an unspecified-kind DateTime and stores exactly the date/time the user picked.
 *
 * Only plain JSON object/array bodies are touched — FormData/Blob (file uploads) and string
 * bodies are left untouched, and the original body object is never mutated (a copy is sent).
 */

function pad(n: number): string {
  return n < 10 ? '0' + n : '' + n;
}

function toLocalIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
         `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function convertDates(value: any): any {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? value : toLocalIso(value);
  }
  if (Array.isArray(value)) {
    return value.map(convertDates);
  }
  if (value && typeof value === 'object') {
    // Don't walk binary / special objects
    if (value instanceof Blob || value instanceof FormData || value instanceof ArrayBuffer) {
      return value;
    }
    const out: any = {};
    for (const key of Object.keys(value)) {
      out[key] = convertDates(value[key]);
    }
    return out;
  }
  return value;
}

export const dateInterceptor: HttpInterceptorFn = (req, next) => {
  const body = req.body;

  const isPlainJsonBody =
    body && typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer) &&
    !(typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams);

  if (isPlainJsonBody) {
    req = req.clone({ body: convertDates(body) });
  }

  return next(req);
};
